import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { submitReview } from '../../services/reviewsService';

const STARS = [1, 2, 3, 4, 5];

export default function ReviewModal({
  visible,
  onClose,
  reviewedUserId,
  reviewedUserName,
  requestId,
  userRole = 'sender',
  onSubmitSuccess,
}) {
  const { t } = useTranslation();
  const [rating, setRating] = useState(0);
  const [comment, setComment] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (visible) {
      setRating(0);
      setComment('');
      setError('');
    }
  }, [visible]);

  if (!visible) {
    return null;
  }

  const handleSubmit = async () => {
    if (!rating) {
      setError(t('reviewModal.selectRatingError'));
      return;
    }

    setSubmitting(true);
    setError('');
    try {
      await submitReview({
        reviewedUserId,
        requestId,
        rating,
        comment,
        role: userRole,
      });
      setSubmitting(false);
      setRating(0);
      setComment('');
      if (onSubmitSuccess) {
        onSubmitSuccess();
      }
      if (onClose) {
        onClose();
      }
    } catch (err) {
      console.error('Error submitting review:', err);
      setError(err.message || t('reviewModal.submitError'));
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div
        className="absolute inset-0 bg-black/60"
        onClick={() => (submitting ? null : onClose?.())}
      />
      <div className="relative w-full max-w-md mx-4 rounded-2xl bg-white dark:bg-slate-900 shadow-2xl border border-gray-200 dark:border-slate-800 p-6">
        <div className="text-center mb-6">
          <h3 className="text-2xl font-extrabold text-gray-900 dark:text-white">
            {t('reviewModal.title')}
          </h3>
          <p className="text-sm text-gray-500 dark:text-slate-400 mt-2">
            {reviewedUserName
              ? t('reviewModal.description', { name: reviewedUserName })
              : t('reviewModal.descriptionFallback')}
          </p>
        </div>

        <div className="flex items-center justify-center gap-2 mb-6">
          {STARS.map(star => (
            <button
              key={star}
              type="button"
              disabled={submitting}
              onClick={() => setRating(star)}
              className={`text-3xl transition ${
                star <= rating
                  ? 'text-yellow-400'
                  : 'text-gray-300 dark:text-slate-600'
              }`}
            >
              {star <= rating ? '★' : '☆'}
            </button>
          ))}
        </div>

        <textarea
          value={comment}
          onChange={e => setComment(e.target.value)}
          rows={4}
          placeholder={t('reviewModal.placeholder')}
          className="w-full rounded-xl border border-gray-200 dark:border-slate-700 bg-gray-50 dark:bg-slate-800 text-gray-900 dark:text-white px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-black dark:focus:ring-yellow-500"
          disabled={submitting}
          maxLength={500}
        />

        {error && (
          <p className="text-sm text-red-500 mt-3" role="alert">
            {error}
          </p>
        )}

        <div className="mt-6 flex gap-3">
          <button
            onClick={() => (submitting ? null : onClose?.())}
            className="flex-1 px-4 py-3 rounded-xl border border-gray-300 dark:border-slate-700 text-gray-700 dark:text-slate-300 font-semibold hover:bg-gray-50 dark:hover:bg-slate-800 transition disabled:opacity-50"
            disabled={submitting}
          >
            {t('common.cancel')}
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || !rating}
            className="flex-1 px-4 py-3 rounded-xl bg-black dark:bg-yellow-500 text-white dark:text-black font-bold hover:bg-gray-900 dark:hover:bg-yellow-400 transition disabled:opacity-50"
          >
            {submitting ? t('reviewModal.submitting') : t('reviewModal.submit')}
          </button>
        </div>
      </div>
    </div>
  );
}
